import { Component, ErrorInfo, ReactNode, useEffect } from 'react';
import { HotkeysProvider } from 'react-hotkeys-hook';
import { Outlet } from 'react-router';
import { toast, Toaster } from 'sonner';

import { AppRoutes } from '@/app/AppRoute';
import { ThemeProvider } from '@/app/ThemeContext';
import { FeaturebaseWidget } from '@/components/FeaturebaseWidget';
import { FloatingWindowHost } from '@/components/FloatingWindow';
import { KeyboardHelpOverlay } from '@/components/KeyboardHelpOverlay';
import { LogRocketIdentifier } from '@/components/LogRocketIdentifier';
import StatsigWrapper from '@/components/statsig/StatsigWrapper';
import { HotkeyScopeSync, INITIAL_ACTIVE_SCOPES } from '@/hooks/hotkeys';
import { useInitAxiosInterceptors } from '@/hooks/useAxios';
import { useVersionCheck } from '@/hooks/useVersionCheck';
import { queryClient } from '@/queryclient';
import AuthProvider, { useAuth } from '@/state/auth';
import { GlobalStateProvider } from '@/state/global.state';
import { QueryClientProvider } from '@/utils/api';

function InitAxiosProvider({ children }: { children: ReactNode }) {
  useInitAxiosInterceptors();

  return children;
}

// Render the `?` cheat sheet only once the user is signed in. Mounted BELOW
// AuthProvider (so it can read auth state) but still inside HotkeysProvider (so
// its `overlay`-scoped shortcut works). Pre-auth it stays unmounted, so `?`
// won't open a sheet listing g-nav shortcuts whose Navigation handlers live
// below AuthProvider and aren't mounted yet (ENG-43).
function AuthedKeyboardHelpOverlay() {
  const { isSignedIn } = useAuth();

  return isSignedIn ? <KeyboardHelpOverlay /> : null;
}

declare global {
  interface Window {
    toastDismiss: typeof toast.dismiss;
  }
}

// FeaturebaseErrorBoundary: Safety net for third-party Featurebase widget
// Prevents the entire app from crashing if the Featurebase SDK throws an error
export class FeaturebaseErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.warn(
      '[Featurebase] Widget error caught by boundary:',
      error,
      errorInfo
    );
  }

  render() {
    if (this.state.hasError) {
      // Widget is non-visual anyway, so just return null
      return null;
    }
    return this.props.children;
  }
}

// ThirdPartyProviders is a wrapper for third-party providers like react-query, react-router, etc.
function ThirdPartyProviders({ children }: { children: ReactNode }) {
  // Declare global type for window to allow toastDismiss property
  useEffect(() => {
    window.toastDismiss = toast.dismiss;
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Toaster richColors position="bottom-right" offset="80px" />
      {children}
    </QueryClientProvider>
  );
}

// AppProviders is a wrapper for providers like AuthProvider, etc that can be used anywhere in the app.
function AppProviders({ children }: { children: ReactNode }) {
  useVersionCheck({
    enableAutoRefresh: import.meta.env.PROD, // Auto-refresh in production only
    notifyUser: true,
    onVersionChange: (oldVersion, newVersion) => {
      // Development: Show toast notification
      if (import.meta.env.DEV) {
        toast.info(`App updated: ${oldVersion} → ${newVersion}`, {
          description: 'Storage cleared for new version',
          duration: 5000,
        });
      }
    },
  });

  return (
    <GlobalStateProvider>
      <HotkeysProvider initiallyActiveScopes={INITIAL_ACTIVE_SCOPES}>
        <HotkeyScopeSync />
        <AuthProvider>
          <AuthedKeyboardHelpOverlay />
          <ThemeProvider>
            <InitAxiosProvider>
              <LogRocketIdentifier />
              {children}
            </InitAxiosProvider>
          </ThemeProvider>
        </AuthProvider>
      </HotkeysProvider>
    </GlobalStateProvider>
  );
}

export function App() {
  return (
    <ThirdPartyProviders>
      <AppRoutes>
        <AppProviders>
          <StatsigWrapper>
            <Outlet />
            {/* Movable/minimizable windows (Constantine exploit feed, etc.).
                Mounted above the router so open windows survive route/drawer
                changes and the KeyedInsights tenant remount. */}
            <FloatingWindowHost />
            {/* FeaturebaseWidget inside StatsigProvider to access gate values */}
            <FeaturebaseErrorBoundary>
              <FeaturebaseWidget />
            </FeaturebaseErrorBoundary>
          </StatsigWrapper>
        </AppProviders>
      </AppRoutes>
    </ThirdPartyProviders>
  );
}
